import { useQueries } from "@tanstack/react-query";
import { GetTransactionsResponse } from "@common/schemas/transaction.schema";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import {
    chunkTransactionIdsForQuery,
    combineTransactionsByIdsQueryResults,
} from "./transactionsByIdsQuery.lib";
import {
    TRANSACTIONS_CACHE_DURATION,
    buildTransactionsQueryKey,
    loadTransactions,
} from "./useTransactionsQuery";

interface UseTransactionsByIdsQueryParams {
    transactionIds: readonly number[];
    entityIds: readonly number[];
    includeMatchDetails?: boolean;
    enabled?: boolean;
}

export function useTransactionsByIdsQuery({
    transactionIds,
    entityIds,
    includeMatchDetails,
    enabled = true,
}: UseTransactionsByIdsQueryParams) {
    const workspace = useActiveWorkspace();
    const workspaceId = workspace?.id ?? "";
    const chunks = chunkTransactionIdsForQuery(transactionIds);
    const isQueryEnabled =
        enabled &&
        chunks.length > 0 &&
        entityIds.length > 0 &&
        workspaceId !== "";

    return useQueries({
        queries: isQueryEnabled
            ? chunks.map((ids) => ({
                  queryKey: buildTransactionsQueryKey({
                      filters: {
                          entityIds: [...entityIds],
                          transactionIds: ids,
                      },
                      page: 1,
                      limit: ids.length,
                      workspaceId,
                      skipCount: true,
                      includeMatchDetails,
                  }),
                  queryFn: () =>
                      loadTransactions({
                          filters: {
                              entityIds: [...entityIds],
                              transactionIds: ids,
                          },
                          page: 1,
                          limit: ids.length,
                          workspaceId,
                          skipCount: true,
                          includeMatchDetails,
                      }),
                  staleTime: TRANSACTIONS_CACHE_DURATION,
              }))
            : [],
        combine: (results) =>
            combineTransactionsByIdsQueryResults<
                GetTransactionsResponse["data"][number]
            >({
                results,
                transactionIds,
            }),
    });
}
